/**
 * Per-caller assignable-role lists. When a Manager opens a role
 * picker, they see a different set of chips than an Admin or
 * Super Admin does — encoded once here so mobile + web + the
 * Cloud Functions that enforce the gates server-side never
 * disagree.
 *
 * Used by:
 *   - Web `MemberDetailSlideOver` chip grid
 *   - Mobile `RolePickerSheet` `assignable` prop
 *   - `setMemberRole` callable as the server-side gate
 *   - `inviteMember` callable for the same gate at invite time
 */
import type { AssignableRole, RoleKey } from './roles';

/** Super Admin can assign anything except another Super Admin
 *  (ownership transfer goes through a separate flow). */
export const ASSIGNABLE_ROLES_BY_SUPER_ADMIN: AssignableRole[] = [
  'admin',
  'manager',
  'accountant',
  'siteEngineer',
  'supervisor',
  'designer',
  'viewer',
  'client',
];

/** Admin can assign anything EXCEPT Admin or Super Admin. Two
 *  admins shouldn't be able to fight over each other's role. */
export const ASSIGNABLE_ROLES_BY_ADMIN: AssignableRole[] = [
  'manager',
  'accountant',
  'siteEngineer',
  'supervisor',
  'designer',
  'viewer',
  'client',
];

/** Manager — the studio's "operating lieutenant". Manages staff
 *  below them; can't grant Admin / SA / another Manager (peer
 *  protection). */
export const ASSIGNABLE_ROLES_BY_MANAGER: AssignableRole[] = [
  'accountant',
  'siteEngineer',
  'supervisor',
  'designer',
  'viewer',
  'client',
];

/**
 * Compute the right assignable list for a given caller role.
 * Returns an empty list for roles that can't manage team — the
 * UI should hide the role-picker entirely in those cases, but
 * defaulting to `[]` keeps callers from showing a broken picker
 * if they forget the gate.
 */
export function assignableRolesFor(
  callerRole: RoleKey | null | undefined,
): AssignableRole[] {
  switch (callerRole) {
    case 'superAdmin':
      return ASSIGNABLE_ROLES_BY_SUPER_ADMIN;
    case 'admin':
      return ASSIGNABLE_ROLES_BY_ADMIN;
    case 'manager':
      return ASSIGNABLE_ROLES_BY_MANAGER;
    default:
      return [];
  }
}
